<script lang="ts">
  import { fade, fly } from 'svelte/transition';
  import { onMount } from 'svelte';
  import confetti from 'canvas-confetti';

  export let isVisible = false;
  export let onShowParticles: ((show: boolean) => void) | undefined = undefined;
  let typewriterText = '';
  let showSubtext = false;
  let showParticles = false;
  
  const fullText = 'SOLD OUT';
  let currentIndex = 0;

  const triggerConfetti = () => {
    // Create a custom canvas with higher z-index
    const canvas = document.createElement('canvas');
    canvas.style.position = 'fixed';
    canvas.style.top = '0';
    canvas.style.left = '0';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.zIndex = '999999'; // ABOVE EVERYTHING
    canvas.style.pointerEvents = 'none';
    document.body.appendChild(canvas);

    const myConfetti = confetti.create(canvas, { resize: true });

    // Multiple confetti bursts
    const colors = ['#ff4444', '#ffaa44', '#44ff44', '#4444ff', '#ff44ff'];
    
    // Initial burst
    myConfetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: colors
    });

    // Side bursts
    setTimeout(() => {
      myConfetti({
        particleCount: 50,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.8 },
        colors: colors
      });
      myConfetti({
        particleCount: 50,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.8 },
        colors: colors
      });
    }, 250);

    // Final burst
    setTimeout(() => {
      myConfetti({
        particleCount: 80,
        spread: 100,
        origin: { y: 0.4 },
        colors: colors
      });
      
      // Clean up canvas after animation
      setTimeout(() => {
        document.body.removeChild(canvas);
      }, 3000);
    }, 500);
  };

  const typeWriter = () => {
    if (currentIndex < fullText.length) {
      typewriterText += fullText[currentIndex];
      currentIndex++;
      setTimeout(typeWriter, 150);
    } else {
      setTimeout(() => {
        showSubtext = true;
        showParticles = true;
        // Notify parent to show particles at top level
        if (onShowParticles) {
          onShowParticles(true);
        }
      }, 500);
    }
  };

  // Trigger animation when isVisible becomes true
  $: if (isVisible && typewriterText === '') {
    setTimeout(() => {
      // Reset state
      typewriterText = '';
      currentIndex = 0;
      showSubtext = false;
      showParticles = false;
      
      // Start animations
      triggerConfetti();
      typeWriter();
    }, 300);
  }
</script>

{#if isVisible}
  <!-- Dimmed Overlay with Text -->
  <div class="sold-out-overlay" in:fade={{ duration: 600 }}>
    <div class="sold-out-container">
      <div class="typewriter-container">
        <h1 class="typewriter-text">
          {typewriterText}<span class="cursor">|</span>
        </h1>
        
        {#if showSubtext}
          <div class="subtext" in:fly={{ y: 30, duration: 600 }}>
            <p class="secondary">All tickets have found their home 🏠</p>
            <p class="secondary" style="font-size: 0.9em;">(don't worry, the next chapter will be sooner than you think 💚)</p>
          </div>
        {/if}
      </div>
    </div>
  </div>

  <!-- Particles will be rendered at parent level -->
{/if}

<style>
  .sold-out-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.9), rgba(30, 30, 60, 0.9));
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
  }

  .sold-out-container {
    position: relative;
    text-align: center;
    color: white;
    max-width: 90%;
  }

  .typewriter-container {
    position: relative;
  }

  .typewriter-text {
    font-size: 5rem;
    font-weight: 900;
    margin: 0;
    background: linear-gradient(45deg, #ff4444, #ff8844, #ffaa44);
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-shadow: 0 0 30px rgba(255, 68, 68, 0.5);
    font-family: 'Courier New', monospace;
  }

  .cursor {
    animation: blink 1s infinite;
    color: #fff;
    -webkit-text-fill-color: #fff;
  }

  .subtext {
    margin-top: 2rem;
  }

  .subtext p {
    font-size: 1.5rem;
    margin: 0.5rem 0;
    animation: glow 2s ease-in-out infinite alternate;
  }

  .subtext .secondary {
    font-size: 1.2rem;
    opacity: 0.8;
    font-style: italic;
  }

  @keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
  }

  @keyframes glow {
    0% { text-shadow: 0 0 10px rgba(255, 255, 255, 0.5); }
    100% { text-shadow: 0 0 20px rgba(255, 255, 255, 0.8); }
  }

  @keyframes float {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    25% { transform: translateY(-20px) rotate(90deg); }
    50% { transform: translateY(-10px) rotate(180deg); }
    75% { transform: translateY(-30px) rotate(270deg); }
  }

  @media (max-width: 768px) {
    .typewriter-text {
      font-size: 3rem;
    }
    
    .subtext p {
      font-size: 1.2rem;
    }
    
  }
</style> 
